import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    FileText, MapPin, Calendar, DollarSign, AlertCircle, Clock,
    CheckCircle, XCircle, Filter
} from 'lucide-react';
import {useApplications} from '../../../hooks/useDashboard';

// Currency formatter utility
const formatCurrency = (amount, currency = 'KES') => {
    if (!amount && amount !== 0) return 'N/A';

    return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

/**
 * ApplicationHistory Component
 * Displays user's rental application history
 */
const ApplicationHistory = () => {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState(null);
    const {applications, loading, error} = useApplications(statusFilter);

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return <CheckCircle className="h-5 w-5 text-highland-500"/>;
            case 'rejected':
                return <XCircle className="h-5 w-5 text-murram-500"/>;
            case 'pending':
            case 'under_review':
                return <Clock className="h-5 w-5 text-dawn-600"/>;
            default:
                return <FileText className="h-5 w-5 text-soil-600"/>;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'bg-highland-50 text-highland-600';
            case 'rejected':
                return 'bg-murram-100 text-murram-600';
            case 'pending':
            case 'under_review':
                return 'bg-dawn-100/40 text-dawn-600';
            case 'draft':
                return 'bg-soil-100 text-soil-900';
            default:
                return 'bg-jacaranda-100 text-jacaranda-600';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Loading applications...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
                    <AlertCircle className="h-12 w-12 text-murram-500 mx-auto mb-4"/>
                    <h2 className="font-display text-xl font-semibold text-soil-900 mb-2">Error Loading Applications</h2>
                    <p className="text-soil-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-soil-900">Application History</h1>
                    <p className="mt-2 text-soil-600">
                        Track your rental applications ({applications.length} total)
                    </p>
                </div>

                {/* Filter */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-soil-100">
                    <div className="flex items-center space-x-2">
                        <Filter className="h-5 w-5 text-soil-400"/>
                        <select
                            value={statusFilter || 'all'}
                            onChange={(e) => setStatusFilter(e.target.value === 'all' ? null : e.target.value)}
                            className="px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                        >
                            <option value="all">All Applications</option>
                            <option value="draft">Draft</option>
                            <option value="submitted">Submitted</option>
                            <option value="pending">Pending</option>
                            <option value="under_review">Under Review</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                {/* Applications List */}
                {applications.length > 0 ? (
                    <div className="space-y-4">
                        {applications.map((application) => (
                            <div
                                key={application.applicationId}
                                className="bg-white rounded-lg shadow-sm border border-soil-100 overflow-hidden hover:shadow-md transition-shadow"
                            >
                                <div className="p-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-lg font-semibold text-soil-900">
                                                    {application.property?.propertyName}
                                                </h3>
                                                <span
                                                    className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}>
                                                    {application.status?.toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex items-center text-sm text-soil-600">
                                                <MapPin className="h-4 w-4 mr-1"/>
                                                <span>{application.property?.address}</span>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 ml-4">
                                            {getStatusIcon(application.status)}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <p className="text-xs text-soil-400 mb-1">Move-in Date</p>
                                            <div className="flex items-center text-sm font-medium text-soil-900">
                                                <Calendar className="h-4 w-4 mr-1 text-soil-400"/>
                                                {new Date(application.desiredMoveInDate).toLocaleDateString()}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-soil-400 mb-1">Lease Length</p>
                                            <p className="text-sm font-medium text-soil-900">
                                                {application.desiredLeaseLength} months
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-soil-400 mb-1">Rent Amount</p>
                                            <div className="flex items-center text-sm font-medium text-soil-900">
                                                <DollarSign className="h-4 w-4 mr-1 text-soil-400"/>
                                                {formatCurrency(application.desiredRentAmount, 'KES')}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-t border-soil-100 pt-4">
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <p className="text-soil-400">Submitted</p>
                                                <p className="text-soil-900 font-medium">
                                                    {new Date(application.submittedDate).toLocaleDateString()}
                                                </p>
                                            </div>
                                            {application.nextAction && (
                                                <div>
                                                    <p className="text-soil-400">Next Action</p>
                                                    <p className="text-soil-900 font-medium">{application.nextAction}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {application.decisionReason && (
                                        <div className="mt-4 p-3 bg-soil-50 rounded-lg">
                                            <p className="text-xs text-soil-400 mb-1">Decision Reason</p>
                                            <p className="text-sm text-soil-600">{application.decisionReason}</p>
                                        </div>
                                    )}

                                    <div className="mt-4 flex items-center space-x-3">
                                        <button
                                            onClick={() => navigate(`/property/${application.propertyId}`)}
                                            className="px-4 py-2 bg-highland-500 text-white text-sm font-medium rounded-lg hover:bg-highland-600 transition-colors"
                                        >
                                            View Property
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-soil-100">
                        <FileText className="mx-auto h-12 w-12 text-soil-400 mb-4"/>
                        <h3 className="text-lg font-medium text-soil-900 mb-2">
                            {statusFilter ? 'No applications found' : 'No applications yet'}
                        </h3>
                        <p className="text-soil-600 mb-6">
                            {statusFilter
                                ? 'Try adjusting your filter'
                                : 'Apply for properties you\'re interested in'}
                        </p>
                        {!statusFilter && (
                            <button
                                onClick={() => navigate('/listings')}
                                className="inline-flex items-center px-6 py-3 bg-highland-500 text-white rounded-lg font-medium hover:bg-highland-600 transition-colors"
                            >
                                Browse Properties
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApplicationHistory;
